<template>
  <div class="py-4 container-fluid pos-page">
    <div class="row g-3">
      <!-- Productos -->
      <div class="col-lg-8">
        <div class="card pos-card h-100">
          <div class="card-header pb-0 border-0">
            <h6 class="mb-3">POS</h6>
            <div class="row">
              <div class="col-md-12 col-lg-12">
                <input
                  v-model="searchQuery"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Buscar producto..."
                  @input="onSearch"
                />
              </div>
            </div>
          </div>

          <div class="card-body pt-3">
            <div v-if="isLoading" class="text-center py-5">
              <i class="fas fa-spinner fa-spin me-1"></i>
              Cargando productos...
            </div>

            <p v-else-if="!products.length" class="text-muted text-center py-5 mb-0">
              No hay productos con stock disponible.
            </p>

            <div v-else>
              <div class="row g-3">
                <div
                  v-for="product in products"
                  :key="product.id"
                  class="col-6 col-md-4"
                >
                  <div
                    class="pos-product-card cursor-pointer"
                    :class="{ 'pos-product-card--disabled': getAvailableStock(product) <= 0 }"
                    @click="addToCart(product)"
                  >
                    <span class="pos-stock-badge">{{ getAvailableStock(product) }}</span>
                    <div class="pos-product-image">
                      <img
                        v-if="product.image_url"
                        :src="product.image_url"
                        :alt="product.title"
                      />
                      <div v-else class="pos-product-placeholder">
                        <i class="fas fa-image"></i>
                      </div>
                    </div>
                    <div class="pos-product-body">
                      <p class="pos-product-title" :title="product.title">{{ product.title }}</p>
                      <p v-if="product.handle" class="pos-product-ref">Ref: {{ product.handle }}</p>
                      <span class="pos-product-price">{{ formatPrice(product) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="pos-footer">
                <span class="pos-footer-info">{{ paginationLabel }}</span>
                <div class="pos-pagination">
                  <button
                    type="button"
                    class="pos-page-btn"
                    :disabled="currentPage <= 1"
                    @click="fetchProducts(currentPage - 1)"
                  >
                    <i class="fas fa-chevron-left"></i>
                  </button>
                  <span class="pos-page-indicator">{{ currentPage }} / {{ lastPage }}</span>
                  <button
                    type="button"
                    class="pos-page-btn"
                    :disabled="currentPage >= lastPage"
                    @click="fetchProducts(currentPage + 1)"
                  >
                    <i class="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Carrito -->
      <div class="col-lg-4">
        <div class="card pos-cart-card h-100">
          <div class="card-header pb-0 border-0">
            <h6 class="mb-0 text-uppercase text-secondary text-xs font-weight-bolder">
              Carrito de venta
            </h6>
          </div>
          <div class="card-body d-flex flex-column">
            <label class="form-label text-xs text-uppercase text-secondary mb-1">Cliente</label>
            <div class="pos-client-row mb-3">
              <div class="pos-client-select-wrap">
                <select ref="clientSelect" class="form-control select2 w-100" style="width: 100%"></select>
              </div>
              <argon-button
                color="success"
                size="sm"
                class="pos-client-add-btn"
                title="Agregar cliente"
                @click="openClientModal"
              >
                <i class="fa fa-user-plus"></i>
              </argon-button>
            </div>

            <label class="form-label text-xs text-uppercase text-secondary mb-1">Productos</label>
            <div class="pos-cart-items flex-grow-1 mb-3">
              <p v-if="!cart.length" class="text-muted text-sm text-center py-4 mb-0">
                El carrito está vacío
              </p>
              <div
                v-for="item in cart"
                :key="item.productId"
                class="pos-cart-item-card"
              >
                <div class="pos-cart-item-header">
                  <span class="pos-cart-item-title">{{ item.title }}</span>
                  <button type="button" class="pos-cart-remove" @click="removeFromCart(item)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>

                <div class="pos-cart-item-controls">
                  <div class="pos-qty-group">
                    <button type="button" class="pos-qty-btn" @click="decreaseQty(item)">−</button>
                    <input
                      v-model.number="item.quantity"
                      type="number"
                      min="1"
                      :max="item.maxStock"
                      class="pos-qty-input"
                      @change="normalizeQty(item)"
                    />
                    <button type="button" class="pos-qty-btn" @click="increaseQty(item)">+</button>
                  </div>
                  <label class="pos-disc-check">
                    <input v-model="item.applyDiscount" type="checkbox" />
                    <span>Desc.</span>
                  </label>
                </div>

                <div class="pos-cart-item-pricing">
                  <div class="pos-unit-price-field">
                    <label class="pos-field-label">Precio unitario</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :value="formatUnitPriceInput(item.unitPrice)"
                      @input="updateUnitPrice(item, $event.target.value)"
                    />
                  </div>
                  <div class="pos-line-total-field">
                    <label class="pos-field-label">Precio total</label>
                    <div class="pos-line-total-wrap">
                      <span
                        v-if="getLineDiscount(item) > 0"
                        class="pos-line-total-original"
                      >
                        {{ formatMoney(getLineSubtotal(item)) }}
                      </span>
                      <span class="pos-line-total-value">{{ formatMoney(getLineTotal(item)) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="pos-cart-summary">
              <div class="mb-2">
                <label class="form-label text-xs text-secondary mb-1">Método de pago</label>
                <select v-model="paymentMethod" class="form-select form-select-sm">
                  <option value="TRB">TRANSFERENCIA BANCARIA</option>
                  <option value="Addi Payment">ADDI</option>
                  <option value="ePayco">EPAYCO</option>
                  <option value="COD">CONTRAENTREGA</option>
                  <option value="wompi">WOMPI</option>
                  <option value="manual">EFECTIVO</option>
                </select> 
              </div>

              <div class="mb-2">
                <label class="form-label text-xs text-secondary mb-1">Método de envío</label>
                <select v-model="deliveryMethod" class="form-select form-select-sm">
                  <option value="envio_nacional">ENVIO NACIONAL</option>
                  <option value="envio_local">ENVIO LOCAL</option>
                  <option value="retiro_fabrica">RETIRO EN FABRICA</option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label text-xs text-secondary mb-1">Flete</label>
                <input
                  type="text"
                  inputmode="numeric"
                  class="form-control form-control-sm"
                  :value="formatUnitPriceInput(freightAmount)"
                  placeholder="0"
                  @input="updateFreightAmount($event.target.value)"
                />
              </div>

              <div class="mb-3">
                <label class="form-label text-xs text-secondary mb-1">Descuento (%)</label>
                <input
                  v-model.number="discountPercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  class="form-control form-control-sm"
                />
              </div>

              <div class="d-flex justify-content-between mb-1">
                <span class="text-sm text-secondary">Subtotal</span>
                <span class="text-sm">{{ formatMoney(subtotal) }}</span>
              </div>
              <div v-if="discountAmount > 0" class="d-flex justify-content-between mb-1">
                <span class="text-sm text-secondary">Descuento</span>
                <span class="text-sm text-danger">− {{ formatMoney(discountAmount) }}</span>
              </div>
              <div v-if="freightAmount > 0" class="d-flex justify-content-between mb-1">
                <span class="text-sm text-secondary">Flete</span>
                <span class="text-sm">+ {{ formatMoney(freightAmount) }}</span>
              </div>
              <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="text-sm font-weight-bold">TOTAL</span>
                <span class="pos-total-value">{{ formatMoney(total) }}</span>
              </div>

              <div class="mb-2">
                <label class="form-label text-xs text-secondary mb-1">Valor pagado</label>
                <input
                  type="text"
                  inputmode="numeric"
                  class="form-control form-control-sm"
                  :value="formatUnitPriceInput(amountPaid)"
                  placeholder="0"
                  @input="updateAmountPaid($event.target.value)"
                />
              </div>

              <div
                v-if="amountPaid > 0"
                class="d-flex justify-content-between align-items-center mb-3"
              >
                <span class="text-sm font-weight-bold">Cambio a devolver</span>
                <span class="pos-change-value">{{ formatMoney(changeToReturn) }}</span>
              </div>

              <argon-button
                color="success"
                class="w-100"
                :disabled="!cart.length || isSaving"
                @click="saveSale"
              >
                <i class="fas fa-save me-1"></i>
                {{ isSaving ? "Guardando..." : "Guardar" }}
              </argon-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal cliente -->
    <div class="modal fade" id="posClientModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Agregar Cliente</h5>
            <button type="button" class="btn-close" @click="closeClientModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Nombre</label>
              <input v-model="newClient.name" type="text" class="form-control" placeholder="Nombre del cliente" />
            </div>
            <div class="mb-3">
              <label class="form-label">Tipo de documento</label>
              <select v-model="newClient.document_type" class="form-select">
                <option value="" disabled>Seleccione</option>
                <option value="CC">CC</option>
                <option value="CE">CE</option>
                <option value="NIT">NIT</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Documento</label>
              <input v-model="newClient.document" type="text" class="form-control" placeholder="Número de documento" />
            </div>
            <div class="mb-3">
              <label class="form-label">Teléfono</label>
              <input v-model="newClient.phone" type="text" class="form-control" placeholder="Teléfono" />
            </div>
            <div class="mb-3">
              <label class="form-label">Email</label>
              <input v-model="newClient.email" type="email" class="form-control" placeholder="Email" />
            </div>
            <div class="mb-3">
              <label class="form-label">Dirección</label>
              <input v-model="newClient.address" type="text" class="form-control" placeholder="Dirección" />
            </div>
            <div class="mb-3">
              <label class="form-label">Departamento <small class="text-muted">(Opcional)</small></label>
              <select v-model="newClient.province" class="form-select" @change="onProvinceChange">
                <option value="">Seleccione un departamento</option>
                <option v-for="dept in departments" :key="dept.code" :value="dept.name">{{ dept.name }}</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Ciudad <small class="text-muted">(Opcional)</small></label>
              <select v-model="newClient.city" class="form-select" :disabled="!newClient.province">
                <option value="">{{ newClient.province ? 'Seleccione una ciudad' : 'Primero seleccione un departamento' }}</option>
                <option v-for="city in availableCities" :key="city" :value="city">{{ city }}</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Código Postal <small class="text-muted">(Opcional)</small></label>
              <input v-model="newClient.zip" type="text" class="form-control" placeholder="Ej: 540001" />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary btn-sm" @click="closeClientModal">Cerrar</button>
            <button type="button" class="btn btn-primary btn-sm" @click="saveClient">Guardar Cliente</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal selección de variantes -->
    <div class="modal fade" id="posVariantModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Seleccionar Variante</h5>
            <button type="button" class="btn-close" @click="closeVariantModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedProductForVariants" class="mb-3">
              <p class="fw-bold mb-2">{{ selectedProductForVariants.title }}</p>
              <p class="text-muted small mb-3">Seleccione una opción:</p>
              
              <div class="variant-list">
                <div
                  v-for="variant in selectedProductForVariants.variants"
                  :key="variant.id"
                  class="variant-item"
                  :class="{ 
                    'selected': selectedVariant?.id === variant.id,
                    'disabled': variant.inventory_quantity <= 0 
                  }"
                  @click="variant.inventory_quantity > 0 && selectVariant(variant)"
                >
                  <div class="variant-info">
                    <span class="variant-title">{{ variant.title }}</span>
                    <span v-if="variant.sku" class="variant-sku">SKU: {{ variant.sku }}</span>
                  </div>
                  <div class="variant-details">
                    <span class="variant-price">${{ Number(variant.price || 0).toLocaleString('es-CO') }}</span>
                    <span 
                      class="variant-stock"
                      :class="{ 'text-danger': variant.inventory_quantity <= 0, 'text-success': variant.inventory_quantity > 0 }"
                    >
                      Stock: {{ variant.inventory_quantity }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary btn-sm" @click="closeVariantModal">Cancelar</button>
            <button 
              type="button" 
              class="btn btn-primary btn-sm" 
              @click="addVariantToCart"
              :disabled="!selectedVariant"
            >
              Agregar al Carrito
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import shopifyService from "@/services/shopify.service";
import userService from "@/services/user.service";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2/dist/css/select2.css";
import "select2";

export default {
  name: "ShopifyPos",
  components: { ArgonButton },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      products: [],
      searchQuery: "",
      currentPage: 1,
      lastPage: 1,
      perPage: 9,
      productsTotal: 0,
      from: 0,
      to: 0,
      searchTimeout: null,
      clients: [],
      selectedClientId: "",
      cart: [],
      paymentMethod: "Cash on Delivery (COD)",
      deliveryMethod: "envio_nacional",
      freightAmount: 0,
      discountPercent: 0,
      amountPaid: 0,
      clientModal: null,
      variantModal: null,
      selectedProductForVariants: null,
      selectedVariant: null,
      newClient: {
        name: "",
        document_type: "CC",
        document: "",
        address: "",
        province: "",
        city: "",
        zip: "",
        phone: "",
        email: "",
        role: "4",
        status: "1",
        password: "cliente123",
        password_confirmation: "cliente123",
        payment: "0",
        processes: [],
      },
      // Lista de departamentos de Colombia con sus ciudades
      departments: [
        { code: 'AMA', name: 'Amazonas', cities: ['Leticia', 'Puerto Nariño', 'El Encanto', 'La Chorrera', 'La Pedrera', 'Miriti-Paraná', 'Puerto Alegría', 'Puerto Arica', 'Puerto Santander', 'Tarapacá'] },
        { code: 'ANT', name: 'Antioquia', cities: ['Medellín', 'Abejorral', 'Abriaquí', 'Alejandría', 'Amagá', 'Amalfi', 'Andes', 'Angelópolis', 'Angostura', 'Anorí', 'Santa Fe de Antioquia', 'Anza', 'Apartadó', 'Arboletes', 'Argelia', 'Armenia', 'Barbosa', 'Bello', 'Betania', 'Betulia', 'Ciudad Bolívar', 'Briceño', 'Buriticá', 'Cáceres', 'Caicedo', 'Caldas', 'Campamento', 'Cañasgordas', 'Caracolí', 'Caramanta', 'Carepa', 'El Carmen de Viboral', 'Carolina', 'Caucasia', 'Chigorodó', 'Cisneros', 'Cocorná', 'Concepción', 'Concordia', 'Copacabana', 'Dabeiba', 'Donmatías', 'Ebéjico', 'El Bagre', 'Entrerríos', 'Envigado', 'Fredonia', 'Frontino', 'Giraldo', 'Girardota', 'Gómez Plata', 'Granada', 'Guadalupe', 'Guarne', 'Guatapé', 'Heliconia', 'Hispania', 'Itagüí', 'Ituango', 'Jardín', 'Jericó', 'La Ceja', 'La Estrella', 'La Pintada', 'La Unión', 'Liborina', 'Maceo', 'Marinilla', 'Montebello', 'Murindó', 'Mutatá', 'Nariño', 'Nechí', 'Necoclí', 'Olaya', 'Peñol', 'Peque', 'Pueblorrico', 'Puerto Berrío', 'Puerto Nare', 'Puerto Triunfo', 'Remedios', 'Retiro', 'Rionegro', 'Sabanalarga', 'Sabaneta', 'Salgar', 'San Andrés de Cuerquia', 'San Carlos', 'San Francisco', 'San Jerónimo', 'San José de la Montaña', 'San Juan de Urabá', 'San Luis', 'San Pedro de los Milagros', 'San Pedro de Urabá', 'San Rafael', 'San Roque', 'San Vicente Ferrer', 'Santa Bárbara', 'Santo Domingo', 'Segovia', 'Sonsón', 'Sopetrán', 'Támesis', 'Titiribí', 'Turbo', 'Uramita', 'Urrao', 'Valdivia', 'Valparaíso', 'Vegachí', 'Venecia', 'Vigía del Fuerte', 'Yalí', 'Yarumal', 'Yolombó', 'Yondó', 'Zaragoza'] },
        { code: 'ARA', name: 'Arauca', cities: ['Arauca', 'Arauquita', 'Cravo Norte', 'Fortul', 'Puerto Rondón', 'Saravena', 'Tame'] },
        { code: 'ATL', name: 'Atlántico', cities: ['Barranquilla', 'Baranoa', 'Campo de la Cruz', 'Candelaria', 'Galapa', 'Juan de Acosta', 'Luruaco', 'Malambo', 'Manatí', 'Palmar de Varela', 'Piojó', 'Polonuevo', 'Ponedera', 'Puerto Colombia', 'Repelón', 'Sabanagrande', 'Sabanalarga', 'Santa Lucía', 'Santo Tomás', 'Soledad', 'Suan', 'Tubará', 'Usiacurí'] },
        { code: 'BOL', name: 'Bolívar', cities: ['Cartagena', 'Achí', 'Altos del Rosario', 'Arenal', 'Arjona', 'Arroyohondo', 'Barranco de Loba', 'Calamar', 'Cantagallo', 'Cicuco', 'Córdoba', 'Clemencia', 'El Carmen de Bolívar', 'El Guamo', 'El Peñón', 'Hatillo de Loba', 'Magangué', 'Mahates', 'Margarita', 'María La Baja', 'Montecristo', 'Mompós', 'Morales', 'Norosí', 'Pinillos', 'Regidor', 'Río Viejo', 'San Estanislao', 'San Fernando', 'San Jacinto', 'San Jacinto del Cauca', 'San Juan Nepomuceno', 'San Martín de Loba', 'San Pablo', 'Santa Catalina', 'Santa Rosa', 'Santa Rosa del Sur', 'Simití', 'Soplaviento', 'Talaigua Nuevo', 'Tiquisio', 'Turbaco', 'Turbaná', 'Villanueva', 'Zambrano'] },
        { code: 'BOY', name: 'Boyacá', cities: ['Tunja', 'Almeida', 'Aquitania', 'Arcabuco', 'Belén', 'Berbeo', 'Betéitiva', 'Boavita', 'Boyacá', 'Briceño', 'Buenavista', 'Busbanzá', 'Caldas', 'Campohermoso', 'Cerinza', 'Chinavita', 'Chiquinquirá', 'Chíquiza', 'Chiscas', 'Chita', 'Chitaraque', 'Chivatá', 'Chivor', 'Ciénega', 'Cómbita', 'Coper', 'Corrales', 'Covarachía', 'Cubará', 'Cucaita', 'Cuítiva', 'Duitama', 'El Cocuy', 'El Espino', 'Firavitoba', 'Floresta', 'Gachantivá', 'Gameza', 'Garagoa', 'Guacamayas', 'Guateque', 'Guayatá', 'Güicán', 'Iza', 'Jenesano', 'Jericó', 'La Capilla', 'La Uvita', 'La Victoria', 'Labranzagrande', 'Macanal', 'Maripí', 'Miraflores', 'Mongua', 'Monguí', 'Moniquirá', 'Motavita', 'Muzo', 'Nobsa', 'Nuevo Colón', 'Oicatá', 'Otanche', 'Pachavita', 'Páez', 'Paipa', 'Pajarito', 'Panqueba', 'Pauna', 'Paya', 'Paz de Río', 'Pesca', 'Pisba', 'Puerto Boyacá', 'Quípama', 'Ramiriquí', 'Ráquira', 'Rondón', 'Saboyá', 'Sáchica', 'Samacá', 'San Eduardo', 'San José de Pare', 'San Luis de Gaceno', 'San Mateo', 'San Miguel de Sema', 'San Pablo de Borbur', 'Santa María', 'Santana', 'Santa Sofía', 'Sativanorte', 'Sativasur', 'Siachoque', 'Soatá', 'Socha', 'Socotá', 'Sogamoso', 'Somondoco', 'Sora', 'Soracá', 'Sotaquirá', 'Susacón', 'Sutamarchán', 'Sutatenza', 'Tasco', 'Tenza', 'Tibaná', 'Tibasosa', 'Tinjacá', 'Tipacoque', 'Toca', 'Togüí', 'Tópaga', 'Tota', 'Tununguá', 'Turmequé', 'Tuta', 'Tutazá', 'Úmbita', 'Ventaquemada', 'Villa de Leyva', 'Viracachá', 'Zetaquira'] },
        { code: 'CAL', name: 'Caldas', cities: ['Manizales', 'Aguadas', 'Anserma', 'Aranzazu', 'Belalcázar', 'Chinchiná', 'Filadelfia', 'La Dorada', 'La Merced', 'Manzanares', 'Marmato', 'Marquetalia', 'Marulanda', 'Neira', 'Norcasia', 'Pácora', 'Palestina', 'Pensilvania', 'Riosucio', 'Risaralda', 'Salamina', 'Samaná', 'San José', 'Supía', 'Victoria', 'Villamaría'] },
        { code: 'CAQ', name: 'Caquetá', cities: ['Florencia', 'Albania', 'Belén de los Andaquíes', 'Cartagena del Chairá', 'Currillo', 'El Doncello', 'El Paujil', 'La Montañita', 'Milán', 'Morelia', 'Puerto Rico', 'San José del Fragua', 'San Vicente del Caguán', 'Solano', 'Solita', 'Valparaíso'] },
        { code: 'CAS', name: 'Casanare', cities: ['Yopal', 'Aguazul', 'Chameza', 'Hato Corozal', 'La Salina', 'Maní', 'Monterrey', 'Nunchía', 'Orocué', 'Paz de Ariporo', 'Pore', 'Recetor', 'Sabanalarga', 'Sácama', 'San Luis de Palenque', 'Támara', 'Tauramena', 'Trinidad', 'Villanueva'] },
        { code: 'CAU', name: 'Cauca', cities: ['Popayán', 'Almaguer', 'Argelia', 'Balboa', 'Bolívar', 'Buenos Aires', 'Cajibío', 'Caldono', 'Caloto', 'Corinto', 'El Tambo', 'Florencia', 'Guachené', 'Guapi', 'Inzá', 'Jambaló', 'La Sierra', 'La Vega', 'López de Micay', 'Mercaderes', 'Miranda', 'Morales', 'Padilla', 'Páez', 'Patía', 'Piamonte', 'Piendamó', 'Puerto Tejada', 'Puracé', 'Rosas', 'San Sebastián', 'Santander de Quilichao', 'Santa Rosa', 'Silvia', 'Sotará', 'Suárez', 'Sucre', 'Timbío', 'Timbiquí', 'Toribío', 'Totoró', 'Villa Rica'] },
        { code: 'CES', name: 'Cesar', cities: ['Valledupar', 'Aguachica', 'Agustín Codazzi', 'Astrea', 'Becerril', 'Bosconia', 'Chimichagua', 'Chiriguaná', 'Curumaní', 'El Copey', 'El Paso', 'Gamarra', 'González', 'La Gloria', 'La Jagua de Ibirico', 'La Paz', 'Manaure', 'Pailitas', 'Pelaya', 'Pueblo Bello', 'Río de Oro', 'San Alberto', 'San Diego', 'San Martín', 'Tamalameque'] },
        { code: 'CHO', name: 'Chocó', cities: ['Quibdó', 'Acandí', 'Alto Baudó', 'Atrato', 'Bagadó', 'Bahía Solano', 'Bajo Baudó', 'Bojayá', 'Cértegui', 'Condoto', 'El Atrato', 'El Carmen de Atrato', 'El Carmen del Darién', 'Istmina', 'Juradó', 'Litoral de San Juan', 'Lloró', 'Medio Atrato', 'Medio Baudó', 'Medio San Juan', 'Nóvita', 'Nuquí', 'Río Iró', 'Río Quito', 'Riosucio', 'San José del Palmar', 'Sipí', 'Unguía', 'Unión Panamericana'] },
        { code: 'COR', name: 'Córdoba', cities: ['Montería', 'Ayapel', 'Buenavista', 'Canalete', 'Cereté', 'Chimá', 'Chinú', 'Ciénaga de Oro', 'Cotorra', 'La Apartada', 'Los Córdobas', 'Momil', 'Montelíbano', 'Moñitos', 'Planeta Rica', 'Pueblo Nuevo', 'Puerto Escondido', 'Puerto Libertador', 'Purísima', 'Sahagún', 'San Andrés de Sotavento', 'San Antero', 'San Bernardo del Viento', 'San Carlos', 'San José de Urabá', 'San Pelayo', 'Santa Cruz de Lorica', 'Tierralta', 'Tuchín', 'Valencia'] },
        { code: 'CUN', name: 'Cundinamarca', cities: ['Bogotá', 'Agua de Dios', 'Albán', 'Anapoima', 'Anolaima', 'Apulo', 'Arbeláez', 'Beltrán', 'Bituima', 'Bojacá', 'Cabrera', 'Cachipay', 'Cajicá', 'Caparrapí', 'Caqueza', 'Carmen de Carupa', 'Chaguaní', 'Chía', 'Chipaque', 'Choachí', 'Chocontá', 'Cogua', 'Cota', 'Cucunubá', 'El Colegio', 'El Peñón', 'El Rosal', 'Facatativá', 'Fómeque', 'Fosca', 'Funza', 'Fúquene', 'Fusagasugá', 'Gachalá', 'Gachancipá', 'Gachetá', 'Gama', 'Girardot', 'Granada', 'Guachetá', 'Guaduas', 'Guasca', 'Guataquí', 'Guatavita', 'Guayabal de Síquima', 'Guayabetal', 'Gutiérrez', 'Jerusalén', 'Junín', 'La Calera', 'La Mesa', 'La Palma', 'La Peña', 'Lenguazaque', 'Machetá', 'Madrid', 'Manta', 'Medina', 'Mosquera', 'Nariño', 'Nemocón', 'Nilo', 'Nimaima', 'Nocaima', 'Pacho', 'Paime', 'Pandi', 'Paratebueno', 'Pasca', 'Puerto Salgar', 'Pulí', 'Quebradanegras', 'Quetame', 'Quipile', 'Ricaurte', 'San Antonio del Tequendama', 'San Bernardo', 'San Cayetano', 'San Francisco', 'San Juan de Río Seco', 'Sasaima', 'Sesquilé', 'Sibaté', 'Silvania', 'Simijaca', 'Soacha', 'Sopó', 'Subachoque', 'Suesca', 'Supatá', 'Susa', 'Sutatausa', 'Tabio', 'Tausa', 'Tena', 'Tenjo', 'Tibacuy', 'Tibirita', 'Tocaima', 'Tocancipá', 'Topaipí', 'Ubalá', 'Ubaque', 'Ubaté', 'Une', 'Útica', 'Vergara', 'Viani', 'Villagómez', 'Villapinzón', 'Villeta', 'Viotá', 'Yacopí', 'Zipacón', 'Zipaquirá'] },
        { code: 'HUI', name: 'Huila', cities: ['Neiva', 'Acevedo', 'Agrado', 'Aipe', 'Algeciras', 'Altamira', 'Baraya', 'Campoalegre', 'Colombia', 'Elías', 'Garzón', 'Gigante', 'Guadalupe', 'Hobo', 'Íquira', 'Isnos', 'La Argentina', 'La Plata', 'Nátaga', 'Oporapa', 'Paicol', 'Palermo', 'Palestina', 'Pital', 'Pitalito', 'Rivera', 'Saladoblanco', 'San Agustín', 'Santa María', 'Suaza', 'Tarqui', 'Tello', 'Teruel', 'Tesalia', 'Timaná', 'Villavieja', 'Yaguará'] },
        { code: 'LAG', name: 'La Guajira', cities: ['Riohacha', 'Albania', 'Barrancas', 'Dibulla', 'Distracción', 'El Molino', 'Fonseca', 'Hatonuevo', 'La Jagua del Pilar', 'Maicao', 'Manaure', 'San Juan del Cesar', 'Uribia', 'Urumita', 'Villanueva'] },
        { code: 'MAG', name: 'Magdalena', cities: ['Santa Marta', 'Algarrobo', 'Aracataca', 'Ariguaní', 'Cerro San Antonio', 'Chibolo', 'Ciénaga', 'Concordia', 'El Banco', 'El Piñón', 'El Retén', 'Fundación', 'Guamal', 'Nueva Granada', 'Pedraza', 'Pijiño del Carmen', 'Pivijay', 'Plato', 'Pueblo Viejo', 'Remolino', 'Sabanas de San Ángel', 'Salamina', 'San Sebastián de Buenavista', 'San Zenón', 'Santa Ana', 'Santa Bárbara de Pinto', 'Sitionuevo', 'Tenerife', 'Zapayán', 'Zona Bananera'] },
        { code: 'MET', name: 'Meta', cities: ['Villavicencio', 'Acacías', 'Barranca de Upía', 'Cabuyaro', 'Castilla la Nueva', 'Cubará', 'Cumaral', 'El Calvario', 'El Castillo', 'El Dorado', 'Fuente de Oro', 'Granada', 'Guamal', 'La Macarena', 'La Uribe', 'Lejanías', 'Mapiripán', 'Mesetas', 'Puerto Concordia', 'Puerto Gaitán', 'Puerto Lleras', 'Puerto López', 'Puerto Rico', 'Restrepo', 'San Carlos de Guaroa', 'San Juan de Arama', 'San Juanito', 'San Martín', 'Vista Hermosa'] },
        { code: 'NAR', name: 'Nariño', cities: ['Pasto', 'Albán', 'Aldana', 'Ancuyá', 'Arboleda', 'Barbacoas', 'Belén', 'Buesaco', 'Chachagüí', 'Colón', 'Consacá', 'Contadero', 'Córdoba', 'Cuaspud', 'Cumbal', 'El Charco', 'El Peñol', 'El Rosario', 'El Tambo', 'Francisco Pizarro', 'Funes', 'Guachucal', 'Gualmatán', 'Iles', 'Imués', 'Ipiales', 'La Cruz', 'La Florida', 'La Llanada', 'La Tola', 'La Unión', 'Leiva', 'Linares', 'Los Andes', 'Magüí', 'Mallama', 'Mosquera', 'Nariño', 'Olaya Herrera', 'Ospina', 'Policarpa', 'Potosí', 'Providencia', 'Puerres', 'Pupiales', 'Ricaurte', 'Roberto Payán', 'Samaniego', 'Sandoná', 'San Bernardo', 'San Lorenzo', 'San Pablo', 'San Pedro de Cartago', 'Santa Bárbara', 'Santacruz', 'Sapuyes', 'Taminango', 'Tangua', 'Tumaco', 'Túquerres', 'Yacuanquer'] },
        { code: 'NSA', name: 'Norte de Santander', cities: ['Cúcuta', 'Ábrego', 'Arboledas', 'Bochalema', 'Bucarasica', 'Cácota', 'Cachirá', 'Chinácota', 'Chitagá', 'Convención', 'Cucutilla', 'Durania', 'El Carmen', 'El Tarra', 'El Zulia', 'Gramalote', 'Hacarí', 'Herrán', 'Labateca', 'La Esperanza', 'La Playa', 'Los Patios', 'Lourdes', 'Mutiscua', 'Ocaña', 'Pamplona', 'Pamplonita', 'Puerto Santander', 'Ragonvalia', 'Salazar', 'San Calixto', 'San Cayetano', 'Santiago', 'Sardinata', 'Silos', 'Teorama', 'Tibú', 'Toledo', 'Villa Caro', 'Villa del Rosario'] },
        { code: 'PUT', name: 'Putumayo', cities: ['Mocoa', 'Colón', 'Orito', 'Puerto Asís', 'Puerto Caicedo', 'Puerto Guzmán', 'Puerto Leguízamo', 'San Francisco', 'San Miguel', 'Santiago', 'Sibundoy', 'Valle del Guamuez', 'Villa Garzón'] },
        { code: 'QUI', name: 'Quindío', cities: ['Armenia', 'Buenavista', 'Calarcá', 'Circasia', 'Córdoba', 'Filandia', 'Génova', 'La Tebaida', 'Montenegro', 'Pijao', 'Quimbaya', 'Salento'] },
        { code: 'RIS', name: 'Risaralda', cities: ['Pereira', 'Apía', 'Balboa', 'Belén de Umbría', 'Dosquebradas', 'Guática', 'La Celia', 'La Virginia', 'Marambá', 'Marsella', 'Mistrató', 'Pueblo Rico', 'Quinchía', 'Santa Rosa de Cabal', 'Santuario'] },
        { code: 'SAN', name: 'Santander', cities: ['Bucaramanga', 'Aguada', 'Albania', 'Aratoca', 'Barbosa', 'Barichara', 'Barrancabermeja', 'Betulia', 'Bolívar', 'Cabrera', 'California', 'Capitanejo', 'Carcasí', 'Cepitá', 'Cerrito', 'Charalá', 'Charta', 'Chima', 'Chipatá', 'Cimitarra', 'Concepción', 'Confines', 'Contratación', 'Coromoro', 'Curití', 'El Guaco', 'El Peñón', 'El Playón', 'Encino', 'Enciso', 'Florián', 'Floridablanca', 'Galán', 'Gámbita', 'Girón', 'Guaca', 'Guadalupe', 'Guapotá', 'Guavata', 'Güepsa', 'Hato', 'Jesús María', 'Jordán', 'La Belleza', 'La Paz', 'Landázuri', 'Lebrija', 'Los Santos', 'Macaravita', 'Málaga', 'Matanza', 'Mogotes', 'Molagavita', 'Ocamonte', 'Oiba', 'Onzaga', 'Palmar', 'Palmas del Socorro', 'Páramo', 'Piedecuesta', 'Pinchote', 'Puente Nacional', 'Puerto Parra', 'Puerto Wilches', 'Rionegro', 'Sabana de Torres', 'San Andrés', 'San Benito', 'San Gil', 'San Joaquín', 'San José de Miranda', 'San Miguel', 'San Vicente de Chucurí', 'Santa Bárbara', 'Santa Helena del Opón', 'Simacota', 'Socorro', 'Suaita', 'Sucre', 'Suratá', 'Tona', 'Valle de San José', 'Vélez', 'Vetas', 'Villanueva', 'Zapatoca'] },
        { code: 'SUC', name: 'Sucre', cities: ['Sincelejo', 'Buenavista', 'Caimito', 'Chalán', 'Colosó', 'Coveñas', 'El Roble', 'Galeras', 'Guaranda', 'La Unión', 'Los Palmitos', 'Majagual', 'Morroa', 'Ovejas', 'Palmito', 'Sampués', 'San Benito Abad', 'San Juan de Betulia', 'San Marcos', 'San Onofre', 'San Pedro', 'Santiago de Tolú', 'Sincé', 'Sucre', 'Tolú Viejo'] },
        { code: 'TOL', name: 'Tolima', cities: ['Ibagué', 'Alpujarra', 'Alvarado', 'Ambalema', 'Anzoátegui', 'Armero', 'Ataco', 'Cajamarca', 'Carmen de Apicalá', 'Casabianca', 'Chaparral', 'Coello', 'Coyaima', 'Cunday', 'Dolores', 'Espinal', 'Falan', 'Flandes', 'Fresno', 'Guamo', 'Herveo', 'Honda', 'Icononzo', 'Lérida', 'Mariquita', 'Melgar', 'Murillo', 'Natagaima', 'Ortega', 'Palocabildo', 'Piedras', 'Planadas', 'Prado', 'Purificación', 'Rovira', 'Saldana', 'Saldaña', 'San Antonio', 'San Luis', 'Santa Isabel', 'Suárez', 'Valle de San Juan', 'Venadillo', 'Villahermosa', 'Villarrica'] },
        { code: 'VAL', name: 'Valle del Cauca', cities: ['Cali', 'Alcalá', 'Andalucía', 'Ansermanuevo', 'Bugalagrande', 'Bolívar', 'Buenaventura', 'Caicedonia', 'Calima', 'Candelaria', 'Cartago', 'Dagua', 'El Águila', 'El Cairo', 'El Cerrito', 'El Dovio', 'Florida', 'Ginebra', 'Guacarí', 'Jamundí', 'La Cumbre', 'La Unión', 'La Victoria', 'Obando', 'Palmira', 'Pradera', 'Roldanillo', 'Sevilla', 'Toro', 'Trujillo', 'Tuluá', 'Ulloa', 'Yotoco', 'Yumbo', 'Zarzal'] },
        { code: 'VAU', name: 'Vaupés', cities: ['Mitú', 'Carurú', 'Pacoa', 'Papunaua', 'Taraira', 'Yavaraté'] },
        { code: 'VID', name: 'Vichada', cities: ['Puerto Carreño', 'Cumaribo', 'La Primavera', 'Santa Rosalía'] },
      ],
    };
  },
  watch: {
    total: {
      immediate: true,
      handler(newTotal) {
        this.amountPaid = newTotal;
      },
    },
  },
  computed: {
    paginationLabel() {
      if (!this.productsTotal) return "";
      return `Mostrando ${this.from}-${this.to} de ${this.productsTotal}`;
    },
    subtotal() {
      return this.cart.reduce((sum, item) => sum + this.getLineSubtotal(item), 0);
    },
    discountAmount() {
      return this.cart.reduce((sum, item) => sum + this.getLineDiscount(item), 0);
    },
    total() {
      return Math.max(this.subtotal - this.discountAmount + (Number(this.freightAmount) || 0), 0);
    },
    changeToReturn() {
      return Math.max((Number(this.amountPaid) || 0) - this.total, 0);
    },
    availableCities() {
      if (!this.newClient.province) return [];
      const dept = this.departments.find(d => d.name === this.newClient.province);
      return dept ? dept.cities : [];
    },
  },
  mounted() {
    this.fetchProducts();
    this.loadClients();
  },
  beforeUnmount() {
    const select = $(this.$refs.clientSelect);
    if (select.length && select.hasClass("select2-hidden-accessible")) {
      select.select2("destroy");
    }
  },
  methods: {
    onSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchProducts(1), 350);
    },
    onProvinceChange() {
      // Limpiar ciudad seleccionada cuando cambia el departamento
      this.newClient.city = '';
    },
    async fetchProducts(page = 1) {
      this.isLoading = true;
      try {
        const response = await shopifyService.getPosProducts({
          page,
          search: this.searchQuery,
          per_page: this.perPage,
        });
        const data = response?.data || {};
        this.products = data.data || [];
        this.currentPage = data.current_page || 1;
        this.lastPage = data.last_page || 1;
        this.productsTotal = data.total || 0;
        this.from = data.from || 0;
        this.to = data.to || 0;
      } catch (error) {
        console.error("Error al cargar productos POS:", error);
        this.products = [];
      } finally {
        this.isLoading = false;
      }
    },
    async loadClients() {
      await this.fetchClients();
      this.initClientSelect2();
    },
    async fetchClients() {
      try {
        const response = await userService.getAll("all");
        const users = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        this.clients = users.filter((u) => u.role == "4" || u.role === 4);
      } catch (error) {
        console.error("Error al cargar clientes:", error);
        this.clients = [];
      }
    },
    initClientSelect2(forceRebuild = false) {
      this.$nextTick(() => {
        const select = $(this.$refs.clientSelect);
        if (!select.length) return;

        if (select.hasClass("select2-hidden-accessible")) {
          if (!forceRebuild) {
            if (this.selectedClientId) {
              select.val(this.selectedClientId).trigger("change.select2");
            }
            return;
          }
          select.select2("destroy");
        }

        select.empty();
        select.append('<option value=""></option>');

        this.clients.forEach((client) => {
          const document = client.document || "—";
          const label = `${document} - ${client.name}`;
          select.append(new Option(label, String(client.id), false, false));
        });

        select
          .select2({
            placeholder: "Seleccione un cliente",
            allowClear: true,
            width: "100%",
          })
          .off("change.select2-pos")
          .on("change.select2-pos", () => {
            this.selectedClientId = select.val() || "";
          });

        if (this.selectedClientId) {
          select.val(this.selectedClientId).trigger("change.select2");
        }
      });
    },
    setSelectedClient(clientId) {
      this.selectedClientId = clientId ? String(clientId) : "";
      this.$nextTick(() => {
        const select = $(this.$refs.clientSelect);
        if (select.length) {
          select.val(this.selectedClientId).trigger("change.select2");
        }
      });
    },
    getCartQty(productId) {
      const item = this.cart.find((i) => i.productId === productId);
      return item ? item.quantity : 0;
    },
    getAvailableStock(product) {
      return Math.max(Number(product.inventory_quantity || 0) - this.getCartQty(product.id), 0);
    },
    getUnitPrice(product) {
      const price = product.price_min ?? product.price_max ?? 0;
      return Number(price) || 0;
    },
    addToCart(product) {
      const available = this.getAvailableStock(product);
      if (available <= 0) return;

      // Check if product has variants
      const hasVariants = product.variants_count > 0 || 
        (Array.isArray(product.variants) && product.variants.length > 0);
      
      if (hasVariants) {
        // Open variant selection modal
        this.openVariantModal(product);
        return;
      }

      const existing = this.cart.find((i) => i.productId === product.id);
      if (existing) {
        existing.quantity += 1;
        return;
      }

      this.cart.push({
        productId: product.id,
        shopifyProductId: product.shopify_product_id,
        title: product.title,
        handle: product.handle,
        unitPrice: this.getUnitPrice(product),
        quantity: 1,
        maxStock: Number(product.inventory_quantity || 0),
        applyDiscount: false,
      });
    },
    openVariantModal(product) {
      this.selectedProductForVariants = product;
      this.selectedVariant = null;
      
      const el = document.getElementById("posVariantModal");
      this.variantModal = Modal.getOrCreateInstance(el, {
        backdrop: "static",
        keyboard: false,
      });
      this.variantModal.show();
    },
    closeVariantModal() {
      this.variantModal?.hide();
      this.selectedProductForVariants = null;
      this.selectedVariant = null;
    },
    selectVariant(variant) {
      this.selectedVariant = variant;
    },
    addVariantToCart() {
      if (!this.selectedVariant || !this.selectedProductForVariants) return;
      
      const product = this.selectedProductForVariants;
      const variant = this.selectedVariant;
      
      // Check if this specific variant is already in cart
      const existing = this.cart.find((i) => 
        i.productId === product.id && i.variantId === variant.id
      );
      
      if (existing) {
        existing.quantity += 1;
        this.closeVariantModal();
        return;
      }

      // Build variant title
      const variantTitle = variant.title !== 'Default Title' ? `${product.title} - ${variant.title}` : product.title;

      this.cart.push({
        productId: product.id,
        variantId: variant.id,
        shopifyProductId: product.shopify_product_id,
        shopifyVariantId: variant.id,
        title: variantTitle,
        handle: product.handle,
        unitPrice: Number(variant.price) || this.getUnitPrice(product),
        quantity: 1,
        maxStock: Number(variant.inventory_quantity || 0),
        applyDiscount: false,
        variant: {
          id: variant.id,
          title: variant.title,
          sku: variant.sku,
          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3,
        },
      });
      
      this.closeVariantModal();
    },
    getLineSubtotal(item) {
      return (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0);
    },
    getDiscountPercentValue() {
      return Math.min(Math.max(Number(this.discountPercent) || 0, 0), 100);
    },
    getLineDiscount(item) {
      if (!item.applyDiscount) return 0;
      const pct = this.getDiscountPercentValue();
      if (pct <= 0) return 0;
      return this.getLineSubtotal(item) * (pct / 100);
    },
    getLineTotal(item) {
      return this.getLineSubtotal(item) - this.getLineDiscount(item);
    },
    formatUnitPriceInput(value) {
      const num = Math.round(Number(value) || 0);
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    updateUnitPrice(item, raw) {
      item.unitPrice = parseInt(String(raw).replace(/\D/g, ""), 10) || 0;
    },
    updateAmountPaid(raw) {
      this.amountPaid = parseInt(String(raw).replace(/\D/g, ""), 10) || 0;
    },
    updateFreightAmount(raw) {
      this.freightAmount = parseInt(String(raw).replace(/\D/g, ""), 10) || 0;
    },
    normalizeQty(item) {
      let qty = parseInt(item.quantity, 10) || 1;
      qty = Math.max(1, Math.min(qty, item.maxStock));
      item.quantity = qty;
    },
    increaseQty(item) {
      if (item.quantity < item.maxStock) {
        item.quantity += 1;
      }
    },
    decreaseQty(item) {
      if (item.quantity > 1) {
        item.quantity -= 1;
      } else {
        this.removeFromCart(item);
      }
    },
    removeFromCart(item) {
      this.cart = this.cart.filter((i) => i.productId !== item.productId);
    },
    openClientModal() {
      this.newClient = {
        name: "",
        document_type: "CC",
        document: "",
        address: "",
        province: "",
        city: "",
        zip: "",
        phone: "",
        email: "",
        role: "4",
        status: "1",
        password: "cliente123",
        password_confirmation: "cliente123",
        payment: "0",
        processes: [],
      };
      const el = document.getElementById("posClientModal");
      this.clientModal = Modal.getOrCreateInstance(el);
      this.clientModal.show();
    },
    closeClientModal() {
      this.clientModal?.hide();
    },
    async saveClient() {
      if (!this.newClient.name || !this.newClient.email || !this.newClient.phone || !this.newClient.document) {
        await Swal.fire("Campos requeridos", "Complete nombre, documento, teléfono y email.", "warning");
        return;
      }
      try {
        const response = await userService.createUser(this.newClient);
        if (response.status >= 200 && response.status < 300) {
          await Swal.fire("Cliente creado", "El cliente fue registrado correctamente.", "success");
          await this.fetchClients();
          this.initClientSelect2(true);
          if (response.data?.id) {
            this.setSelectedClient(response.data.id);
          }
          this.closeClientModal();
        }
      } catch (error) {
        await Swal.fire(
          "Error",
          error?.response?.data?.message || "No se pudo crear el cliente.",
          "error"
        );
      }
    },
    async saveSale() {
      if (!this.selectedClientId) {
        await Swal.fire("Cliente requerido", "Seleccione un cliente antes de guardar.", "warning");
        return;
      }
      if (!this.cart.length) {
        await Swal.fire("Carrito vacío", "Agregue productos al carrito.", "warning");
        return;
      }

      this.isSaving = true;
      try {
        const response = await shopifyService.createPosSale({
          user_id: Number(this.selectedClientId),
          payment_method: this.paymentMethod,
          delivery_method: this.deliveryMethod,
          freight_amount: this.freightAmount,
          discount_percent: this.discountPercent,
          amount_paid: this.amountPaid,
          items: this.cart.map((item) => ({
            shopify_inventory_id: item.productId,
            shopify_product_id: item.shopifyProductId,
            title: item.title,
            handle: item.handle,
            quantity: item.quantity,
            unit_price: item.unitPrice,
            apply_discount: !!item.applyDiscount,
            line_subtotal: this.getLineSubtotal(item),
            line_discount: this.getLineDiscount(item),
            line_total: this.getLineTotal(item),
          })),
        });

        const data = response?.data || {};

        if (!data.success) {
          throw new Error(data.message || "No se pudo guardar la venta.");
        }

        const orderId = data.order?.id;
        this.$router.push({ path: '/shopify/pos/ticket', query: { id: orderId } });
      } catch (error) {
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "No se pudo guardar la venta.",
          "error"
        );
      } finally {
        this.isSaving = false;
      }
    },
    getPaymentMethodLabel(method) {
      const labels = {
        "Cash on Delivery (COD)": "TRANSFERENCIA BANCARIA",
        "Addi Payment": "ADDI",
        "ePayco": "EPAYCO",
        "Contra Entrega: abona $20.000 y paga el resto al recibir": "CONTRAENTREGA",
        "wompi": "WOMPI",
        "manual": "EFECTIVO",
      };
      return labels[method] || method;
    },
    getDeliveryMethodLabel(method) {
      const labels = {
        envio_nacional: "ENVÍO NACIONAL",
        envio_local: "ENVÍO LOCAL",
        retiro_fabrica: "RETIRO EN FÁBRICA",
      };
      return labels[method] || method;
    },
    formatPrice(product) {
      const min = product.price_min;
      const max = product.price_max;
      if (min == null && max == null) return "$ 0,00";
      const formatter = new Intl.NumberFormat("es-CO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      if (min != null && max != null && Number(min) !== Number(max)) {
        return `$ ${formatter.format(min)} – ${formatter.format(max)}`;
      }
      return `$ ${formatter.format(min ?? max)}`;
    },
    formatMoney(value) {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value) || 0);
    },
  },
};
</script>

<style scoped>
.pos-page {
  background: #f4f6f9;
}

.pos-card,
.pos-cart-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.pos-cart-card {
  position: sticky;
  top: 80px;
}

.cursor-pointer {
  cursor: pointer;
}

.pos-product-card {
  position: relative;
  border: 2px solid #000000;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
}

.pos-product-card:hover:not(.pos-product-card--disabled) {
  box-shadow: 0 4px 14px rgba(229, 57, 53, 0.2);
  transform: translateY(-2px);
}

.pos-product-card--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pos-stock-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border-radius: 50%;
  background: #000000;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pos-product-image {
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background: #fafafa;
}

.pos-product-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.pos-product-placeholder {
  color: #ccc;
  font-size: 2rem;
}

.pos-product-body {
  padding: 10px 12px 14px;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pos-product-title {
  font-size: 0.78rem;
  font-weight: 700;
  margin: 0 0 4px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
}

.pos-product-ref {
  font-size: 0.68rem;
  color: #8898aa;
  margin: 0 0 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.pos-product-price {
  display: inline-block;
  margin-top: auto;
  padding: 4px 14px;
  border-radius: 20px;
  background: #2dce89;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
}

.pos-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.pos-footer-info {
  font-size: 0.85rem;
  color: #8898aa;
}

.pos-pagination {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pos-page-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  color: #525f7f;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pos-page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pos-page-indicator {
  font-size: 0.85rem;
  color: #525f7f;
}

.pos-client-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 8px;
}

.pos-client-select-wrap {
  flex: 1;
  min-width: 0;
}

.pos-client-select-wrap :deep(.select2-container) {
  width: 100% !important;
}

.pos-client-add-btn {
  flex: 0 0 auto;
  width: auto !important;
  min-width: 42px;
  margin: 0 !important;
  padding-left: 12px;
  padding-right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
}

.pos-cart-items {
  max-height: 340px;
  overflow-y: auto;
  padding: 2px;
}

.pos-cart-item-card {
  border: 1px solid #dee2e6;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fff;
}

.pos-cart-item-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
}

.pos-local-badge {
  flex-shrink: 0;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 2px 6px;
  border-radius: 4px;
  background: #5e72e4;
  color: #fff;
}

.pos-cart-item-title {
  flex: 1;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.25;
  color: #32325d;
  word-break: break-word;
}

.pos-cart-remove {
  flex-shrink: 0;
  border: none;
  background: none;
  color: #f5365c;
  cursor: pointer;
  padding: 0 2px;
  font-size: 0.85rem;
  line-height: 1;
}

.pos-cart-item-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.pos-qty-group {
  display: flex;
  align-items: stretch;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.pos-qty-btn {
  width: 32px;
  border: none;
  background: #f8f9fe;
  color: #525f7f;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}

.pos-qty-input {
  width: 44px;
  border: none;
  border-left: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 4px 2px;
  -moz-appearance: textfield;
}

.pos-qty-input::-webkit-outer-spin-button,
.pos-qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.pos-disc-check {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 0.8rem;
  color: #525f7f;
  cursor: pointer;
  user-select: none;
}

.pos-disc-check input {
  cursor: pointer;
}

.pos-cart-item-pricing {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.pos-unit-price-field {
  flex: 1;
  min-width: 0;
}

.pos-line-total-field {
  flex: 1;
  min-width: 0;
  text-align: right;
}

.pos-field-label {
  display: block;
  font-size: 0.68rem;
  color: #8898aa;
  margin-bottom: 4px;
}

.pos-line-total-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.pos-line-total-original {
  font-size: 0.72rem;
  color: #8898aa;
  text-decoration: line-through;
}

.pos-line-total-value {
  display: block;
  font-size: 1rem;
  font-weight: 800;
  color: #2dce89;
  line-height: 1.2;
}

.pos-cart-item {
  padding: 8px 4px;
  border-bottom: 1px solid #e9ecef;
}

.pos-cart-item:last-child {
  border-bottom: none;
}

.pos-cart-item-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 6px 0;
}

.pos-qty-value {
  min-width: 20px;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
}

.pos-remove-btn {
  margin-left: auto;
  border: none;
  background: none;
  color: #f5365c;
  cursor: pointer;
  font-size: 0.8rem;
}

.pos-cart-item-total {
  font-size: 0.8rem;
  font-weight: 700;
  text-align: right;
}

.pos-total-value {
  font-size: 1.1rem;
  font-weight: 800;
  color: #2dce89;
}

.pos-change-value {
  font-size: 1rem;
  font-weight: 800;
  color: #5e72e4;
}

.pos-cart-summary {
  border-top: 1px solid #e9ecef;
  padding-top: 12px;
}

/* Variant Selection Modal Styles */
.variant-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 8px;
}

.variant-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.variant-item:last-child {
  margin-bottom: 0;
}

.variant-item:hover:not(.disabled) {
  border-color: #5e72e4;
  background-color: #f8f9fa;
}

.variant-item.selected {
  border-color: #5e72e4;
  background-color: #e8eaf6;
}

.variant-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f8f9fa;
}

.variant-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.variant-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #32325d;
}

.variant-sku {
  font-size: 0.75rem;
  color: #8898aa;
}

.variant-details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.variant-price {
  font-weight: 700;
  font-size: 1rem;
  color: #2dce89;
}

.variant-stock {
  font-size: 0.8rem;
  font-weight: 500;
}

</style>
